<template>
    <component
        :is="props.is"
        v-bind="
            props.is === BaseLink
                ? {
                      to: props.to,
                      externalTo: props.externalTo,
                      target: props.target,
                  }
                : {}
        "
        :class="{
            sqrcl: props.sqrcl,
            [$style.cardContainer]: true,
            [$style.responsive]: props.responsive,
            [$style.cardVertical]: effectiveAppearance === 'vertical',
            [$style.bigCard]: effectiveAppearance === 'big_horizontal',
            [$style.icon]: effectiveAppearance === 'icon',
            [$style.placeholder]: !!effectiveSrc || props.placeholder,
        }"
    >
        <img
            v-if="effectiveSrc"
            :key="
                props.lazy
                    ? `${effectiveSrc}_${effectiveAppearance}`
                    : undefined /* remount the image if it is lazy, otherwise nobody update src */
            "
            :class="props.imageClass"
            data-allow-mismatch
            :alt="props.alt"
            v-bind="{
                ...(props.lazy
                    ? {
                          'data-src': effectiveSrc,
                          'data-srcset': props.srcset,
                          'data-sizes': props.sizes,
                          'data-expand': props.expand,
                          class: [$style.img, 'lazyload'],
                          loading: 'lazy',
                      }
                    : {
                          src: effectiveSrc,
                          class: [$style.img, 'lazyloaded'],
                          fetchpriority: 'high',
                          srcset: srcSet,
                          sizes: props.sizes,
                      }),
                ...(props.itemprop ? { itemprop: 'image' } : {}),
            }"
        />
        <img
            v-if="iconSrc"
            :key="props.lazy ? `${iconSrc}` : undefined"
            :class="props.iconImageClass"
            data-allow-mismatch
            :alt="props.alt"
            v-bind="{
                ...(props.lazy
                    ? {
                          src: iconSrc,
                          class: [$style.img, 'lazyloaded'],
                          loading: 'lazy',
                      }
                    : {
                          src: iconSrc,
                          class: [$style.img, 'lazyloaded'],
                          fetchpriority: 'high',
                      }),
                ...(props.itemprop ? { itemprop: 'image' } : {}),
            }"
        />

        <slot />
    </component>
</template>

<script setup lang="ts">
import 'lazysizes'
import '@/utils/lazysizes-pg-thumbs-plugin.side-effect'

import { type ReactiveHead, useHead } from '@unhead/vue'
import { type HTMLAttributes, computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import BaseLink from '@/components/BaseLink.vue'
import type { LinkTarget } from '@/types'
import { processThumbSrcset } from '@/utils/pg-thumbs-helpers'

type VueClassBinding = string | Record<string, unknown> | (string | Record<string, unknown>)[]
export type CardAppearance = 'big_horizontal' | 'medium_horizontal' | 'icon' | 'vertical'

interface CardContainerProps extends /* @vue-ignore */ Omit<HTMLAttributes, 'placeholder' | 'is'> {
    is?: 'div' | typeof BaseLink
    to?: RouteLocationRaw
    externalTo?: RouteLocationRaw
    appearance?: CardAppearance
    approximateWidth?: number
    target?: LinkTarget
    src?: string
    srcset?: string
    sizes?: string
    alt?: string
    srcsetOn?: boolean
    responsive?: boolean
    lazy?: boolean
    expand?: number
    preload?: boolean
    placeholder?: boolean
    /*  iconSrc + iconImageClass + imageClass goes together */
    iconSrc?: string
    iconImageClass?: VueClassBinding
    imageClass?: VueClassBinding
    itemprop?: string
    sqrcl?: boolean
}

const props = withDefaults(defineProps<CardContainerProps>(), {
    is: 'div',
    appearance: 'medium_horizontal',
    srcsetOn: true,
    lazy: true,
    sqrcl: true,
})

const effectiveAppearance = computed(() => props.appearance)
const effectiveSrc = computed(() => props.src)

const srcSet = computed(() => {
    if (props.srcset) {
        return props.srcset
    }
    if (props.srcsetOn && !props.lazy && effectiveSrc.value) {
        let approximateWidth = 160

        if (props.appearance === 'icon') {
            approximateWidth = 90
        }
        if (props.appearance === 'big_horizontal') {
            approximateWidth = approximateWidth * 2
        }
        if (props.approximateWidth) {
            approximateWidth = props.approximateWidth
        }
        return processThumbSrcset(effectiveSrc.value, approximateWidth)
    }
    return ''
})

const preloadHeadLink = computed(() => {
    const link: ReactiveHead['link'] = []
    if (props.preload && effectiveSrc.value) {
        link.push({
            rel: 'preload',
            as: 'image',
            href: effectiveSrc.value,
            imagesrcset: srcSet.value,
            imagesizes: props.sizes,
            fetchpriority: 'high',
        })
    }
    return {
        link,
    }
})

useHead(preloadHeadLink)
</script>

<style module>
.cardContainer {
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 100%;
    grid-template-areas: 'main';
    background-color: var(--carbon-400);
    width: 230px;
    aspect-ratio: var(--thumbnail-aspect-ratio);
    overflow: hidden;
    contain: strict;
    border-radius: 12px;
    --sqrcl: clamp(0rem, 30%, 50rem);
}
.icon {
    aspect-ratio: 1;
}
.responsive {
    width: 100%;
    aspect-ratio: var(--thumbnail-aspect-ratio);
    height: auto;
}

.bigCard.responsive {
    --big-card-width: 320;
    --big-card-height: calc(320 / var(--thumbnail-aspect-ratio));

    aspect-ratio: calc(var(--big-card-width) + var(--cards-gap)) / calc(var(--big-card-height) + var(--cards-gap)); /* same as normal, but with additional 8px gaps */
}

.cardVertical.responsive {
    --vertical-card-width: 246;
    --vertical-card-height: calc(246 / var(--thumbnail-aspect-ratio-vertical));

    aspect-ratio: calc(var(--vertical-card-width) + var(--cards-gap)) /
        calc(var(--vertical-card-height) + var(--cards-gap));
}

.placeholder:not(:has(.img:global(.lazyloaded))) {
    /* it's important to animate opacity, not background to avoid repaint  */
    background-color: var(--white);
    opacity: 0.08;
    animation: blink 1.4s infinite;
}

@keyframes blink {
    0%,
    100% {
        opacity: 0.08;
    }

    38%,
    50% {
        opacity: 0.16;
    }

    88% {
        opacity: 0.08;
    }
}

.img {
    display: block;
    object-fit: cover;
    width: 100%;
    height: 100%;
    grid-area: main;
}

.img:global(.lazyload),
.img:global(.lazyloading) {
    opacity: 0;
}
.img:global(.lazyloaded) {
    opacity: 1;
    transition: opacity ease-out 0.1s;
}
</style>
